/** @format */

import { FilterMtchProps } from 'src/pages/AIObservability/TraceAnalysis/typescript'
import type {
  TraceAnalysisDetailResponse,
  TraceAnalysisListItem
} from 'src/pages/AIObservability/TraceAnalysis/typescript'
import type {
  SessionAnalysisListItem,
  SessionFilterItem
} from 'src/pages/AIObservability/SessionAnalysis/typescript'
import type { SessionDetail } from 'src/pages/AIObservability/SessionAnalysis/SessionDetail/typescript'
import type { OverviewLineChartPoint } from 'src/pages/AIObservability/components/OverviewLineChart'
import request from 'src/utils/request'

/** 公共时间与区域查询参数（GetAppMonitor* 系列接口共用） */
export interface AppMonitorSearchParams {
  /** 服务名称；全局视角可传空字符串 */
  ServiceName?: string
  /** 开始时间，Unix 秒级时间戳 */
  StartTime?: number
  /** 结束时间，Unix 秒级时间戳 */
  EndTime?: number
  /** 地域标识 */
  Region?: string
  /** 指标聚合步长，如 `60s` */
  Step?: string
  /** 排序方向，如 `asc` / `desc` */
  Order?: string
  /** 排序字段 */
  OrderBy?: string
}

/** 分页 + 筛选列表查询参数（会话 / Trace 列表） */
export interface AppMonitorListParams extends AppMonitorSearchParams {
  Page?: number
  PageSize?: number
  /** 侧栏筛选条件；提交前应过滤掉空值项 */
  Filters?: SessionFilterItem[] | FilterMtchProps[]
}

/** GetAppMonitorAPIKey 响应中的 AppInfo */
export interface AppMonitorAppInfo {
  AppKey?: string
  LangfusePublicKey?: string
  LangfuseSecretKey?: string
}

/** GetAppMonitorAPIKey 网关 data 字段 */
export interface AppMonitorApiKeyResponse {
  AppInfo?: AppMonitorAppInfo
}

/** ListAppMonitorOverviews 概览指标 */
export interface AppMonitorOverviewData {
  ServiceCount?: number
  ActiveSessionCount?: number
  TokenTotalUsage?: number
  InvocationCount?: number
  ErrorCount?: number
  ErrorRate?: number
}

/** ListAppMonitorOverviewMetrics 单条时序序列 */
export interface AppMonitorMetricSeries {
  Metric: string
  Points?: OverviewLineChartPoint[]
}

/** ListAppMonitorServices 服务列表项 */
export interface AppMonitorServiceItem {
  Id: string
  ServiceName: string
  LastServiceReportTime?: string
  ServiceLanguage?: string
  IngestionSource?: string
  ServiceType?: string
}

/** ListAppMonitorServices 分页响应 */
export interface AppMonitorServiceListResponse {
  Services?: AppMonitorServiceItem[]
  TotalCount?: number
}

/** GetTraceFilterFields 可筛选字段 */
export interface TraceFilterField {
  Name: string
  Category: string
}

/** GetTraceFilterCandidates 字段候选值 */
export type TraceFilterCandidate = string

/** ListAppMonitorSessions 分页响应 */
export interface AppMonitorSessionListResponse {
  Sessions?: SessionAnalysisListItem[]
  TotalCount?: number
}

/** ListAppMonitorSpans / ListAppMonitorRootSpans 分页响应 */
export interface AppMonitorTraceListResponse {
  Spans?: TraceAnalysisListItem[]
  RootSpans?: TraceAnalysisListItem[]
  TotalCount?: number
}

/** GetTraceFilterCandidates 请求参数 */
export interface TraceFilterCandidatesParams {
  StartTime: number
  EndTime: number
  FieldName: string
  Filters?: FilterMtchProps[]
  Keyword?: string
  Limit?: number
}

/** GetAppMonitorSessionDetail 请求参数 */
export interface SessionDetailParams {
  ServiceName: string
  SessionId: string
}

/** GetAppMonitorTraceDetail 请求参数 */
export interface TraceDetailParams {
  TraceId: string
}

/**
 * 获取应用监控接入密钥（GetAppMonitorAPIKey）。
 * 用于接入中心展示 OTLP AppKey、Langfuse Public/Secret Key。
 *
 * @returns 网关 `data`，含 `AppInfo`（AppKey、Langfuse 密钥等）
 */
export const getAppKey = async (): Promise<AppMonitorApiKeyResponse | undefined> => {
  const result = await request('/monitor/?Action=GetAppMonitorAPIKey&Version=2026-01-01', {
    method: 'GET'
  })
  return result?.data
}

/**
 * 获取服务总览汇总指标（ListAppMonitorOverviews）。
 *
 * @param params - 查询时间与可选服务、地域
 * @param params.ServiceName - 服务名称；不传或空字符串表示全局汇总
 * @param params.StartTime - 开始时间（Unix 秒）
 * @param params.EndTime - 结束时间（Unix 秒）
 * @param params.Region - 地域
 * @returns 会话数、Token 消耗、调用数、错误数等概览字段
 */
export const getOverviews = async (
  params: AppMonitorSearchParams
): Promise<AppMonitorOverviewData | undefined> => {
  const result = await request('/monitor/?Action=ListAppMonitorOverviews&Version=2026-01-01', {
    data: params,
    method: 'GET'
  })
  return result?.data
}

/**
 * 获取服务总览趋势指标时序（ListAppMonitorOverviewMetrics）。
 *
 * @param params - 时间范围、聚合步长及可选服务、地域
 * @param params.Step - 聚合步长，如 `60s`
 * @returns `Series` 数组，每项含 `Metric` 名称与 `Points` 时序点
 */
export const getMetricsData = async (
  params: AppMonitorSearchParams
): Promise<AppMonitorMetricSeries[] | undefined> => {
  const result = await request(
    '/monitor/?Action=ListAppMonitorOverviewMetrics&Version=2026-01-01',
    {
      data: params,
      method: 'GET'
    }
  )
  return result?.data?.Series
}

/**
 * 分页查询服务列表（ListAppMonitorServices）。
 *
 * @param params - 时间范围、分页、排序及服务名模糊搜索
 * @param params.ServiceName - 服务名称关键字
 * @param params.Page - 页码
 * @param params.PageSize - 每页条数
 * @returns 含 `Services` 列表与 `TotalCount` 总数
 */
export const getServiceList = async (
  params: AppMonitorSearchParams & Pick<AppMonitorListParams, 'Page' | 'PageSize'>
): Promise<AppMonitorServiceListResponse | undefined> => {
  const result = await request('/monitor/?Action=ListAppMonitorServices&Version=2026-01-01', {
    data: params,
    method: 'GET'
  })
  return result?.data
}

/**
 * 获取 Trace Span 可筛选属性字段（GetTraceFilterFields）。
 *
 * @param params - 时间范围；用于限定候选属性来源
 * @returns 字段列表，每项含 `Name`、`Category`（如 `span_attr`）
 */
export const getTraceSpanAttr = async (
  params: Pick<AppMonitorSearchParams, 'StartTime' | 'EndTime'>
): Promise<TraceFilterField[]> => {
  const result = await request('/monitor/?Action=GetTraceFilterFields&Version=2026-01-01', {
    data: params,
    method: 'POST'
  })
  return result?.data?.Fields || []
}

/**
 * 获取 Trace 筛选字段的可选值（GetTraceFilterCandidates）。
 *
 * @param params - 时间范围、字段名及已选筛选条件
 * @param params.FieldName - 属性字段名，与 GetTraceFilterFields 返回的 Name 一致
 * @param params.Filters - 已生效的其它筛选条件，用于联动缩小候选集
 * @returns 候选值字符串数组
 */
export const getFieldOptions = async (
  params: TraceFilterCandidatesParams
): Promise<TraceFilterCandidate[]> => {
  const result = await request('/monitor/?Action=GetTraceFilterCandidates&Version=2026-01-01', {
    data: params,
    method: 'POST'
  })
  return result?.data?.Candidates || []
}

/**
 * 分页查询会话列表（ListAppMonitorSessions）。
 *
 * @param params - 时间范围、分页、排序及 Filters 侧栏筛选
 * @returns 含 `Sessions` 列表与 `TotalCount` 总数
 */
export const getSessionList = async (
  params: AppMonitorListParams
): Promise<AppMonitorSessionListResponse | undefined> => {
  const result = await request('/monitor/?Action=ListAppMonitorSessions&Version=2026-01-01', {
    data: params,
    method: 'POST'
  })
  return result?.data
}

/**
 * 获取单会话详情（GetAppMonitorSessionDetail）。
 *
 * @param params - 服务名与会话 ID
 * @returns 会话统计、RootSpans、SessionTraces 等；无数据时返回空对象
 */
export const getSessionDetail = async (params: SessionDetailParams): Promise<SessionDetail> => {
  const result = await request('/monitor/?Action=GetAppMonitorSessionDetail&Version=2026-01-01', {
    data: params,
    method: 'GET'
  })
  return result?.data || {}
}

/**
 * 分页查询 Trace Span 列表（ListAppMonitorSpans，含非 Root Span）。
 *
 * @param params - 时间范围、分页、排序及 Span 属性 Filters
 * @returns 含 `Spans` 列表与 `TotalCount`；无数据时返回空对象
 */
export const getTraceSpanList = async (
  params: AppMonitorListParams
): Promise<AppMonitorTraceListResponse> => {
  const result = await request('/monitor/?Action=ListAppMonitorSpans&Version=2026-01-01', {
    data: params,
    method: 'POST'
  })
  return result?.data || {}
}

/**
 * 分页查询 Trace Root Span 列表（ListAppMonitorRootSpans）。
 *
 * @param params - 与 {@link getTraceSpanList} 相同
 * @returns 含 `Spans`（每条对应一条 Trace 根 Span）与 `TotalCount`
 */
export const getTraceRootSpanList = async (
  params: AppMonitorListParams
): Promise<AppMonitorTraceListResponse> => {
  const result = await request('/monitor/?Action=ListAppMonitorRootSpans&Version=2026-01-01', {
    data: params,
    method: 'POST'
  })
  return result?.data || {}
}

/**
 * 获取单条链路 Trace 详情（GetAppMonitorTraceDetail）。
 *
 * @param params - 服务名与 TraceId
 * @returns 链路元信息、嵌套 `Spans` 树及可选 `WorkflowGraph` 工作流拓扑；无数据时返回空对象
 */
export const getTraceDetail = async (
  params: TraceDetailParams
): Promise<TraceAnalysisDetailResponse> => {
  const result = await request('/monitor/?Action=GetAppMonitorTraceDetail&Version=2026-01-01', {
    data: params,
    method: 'GET'
  })
  return result?.data || {}
}

// 获取Trace事件列表
export interface TraceEventLogItem {
  Time: string
  Message: string
  Fields?: {
    event_name?: string
    time_unix_nano?: string
    observed_time_unix_nano?: string
    severity_text?: string
    body?: string
    [key: string]: string | undefined
  }
}

export interface TraceEventResult {
  Logs: TraceEventLogItem[]
}
interface TraceEventParams {
  TraceId: string
  StartTime: number
  EndTime: number
}
export const getTraceEventList = async (params: TraceEventParams): Promise<TraceEventResult> => {
  const result = await request('/monitor/?Action=GetAppMonitorTraceLogs&Version=2026-01-01', {
    data: params,
    method: 'GET'
  })
  return result?.data || {}
}
